import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Sparkles, X, Crown } from 'lucide-react';
import { useSubscription } from '@/context/SubscriptionContext';
import { useAuth } from '@/context/AuthContext';

const TrialBanner = () => {
  const { user } = useAuth();
  const { 
    isTrialActive, 
    isTrialExpired, 
    trialRemainingSeconds, 
    isPremium,
    tier,
    loading 
  } = useSubscription();
  
  const [timeRemaining, setTimeRemaining] = useState(trialRemainingSeconds);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setTimeRemaining(trialRemainingSeconds);
  }, [trialRemainingSeconds]);

  useEffect(() => {
    if (!isTrialActive || timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isTrialActive, timeRemaining]);

  const formatTime = (seconds) => {
    if (seconds <= 0) return '0:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m ${secs}s`;
  };

  const getUrgencyLevel = () => {
    if (timeRemaining <= 3600) return 'critical';
    if (timeRemaining <= 7200) return 'warning';
    return 'normal';
  };

  if (loading || dismissed) return null;
  if (isPremium) return null;

  // Anonymous users - show registration prompt
  if (!user) {
    return (
      <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 text-white py-3 px-4 relative overflow-hidden">
        <div className="max-w-7xl mx-auto flex items-center justify-between relative z-10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-lg">
              <Sparkles className="h-5 w-5" />
            </div>
            <div>
              <p className="font-bold text-sm sm:text-base">Get 48 hours FREE full access!</p>
              <p className="text-xs sm:text-sm text-white/80">Register now to unlock all features</p>
            </div>
          </div>
          <Link 
            to="/register" 
            className="bg-white text-indigo-600 px-4 py-2 rounded-full text-sm font-bold hover:bg-indigo-50 transition-all shadow-lg"
            data-testid="trial-register-btn"
          >
            Start Free Trial
          </Link>
        </div>
      </div>
    );
  }

  // Active trial countdown
  if (isTrialActive && timeRemaining > 0) {
    const urgency = getUrgencyLevel();
    const bgClass = urgency === 'critical' 
      ? 'bg-gradient-to-r from-red-600 via-orange-600 to-amber-500' 
      : urgency === 'warning'
      ? 'bg-gradient-to-r from-amber-500 via-orange-500 to-red-500'
      : 'bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500';

    return (
      <div className={`${bgClass} text-white py-3 px-4 relative overflow-hidden`} data-testid="trial-banner">
        <div className="max-w-7xl mx-auto flex items-center justify-between relative z-10">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${urgency === 'critical' ? 'bg-white/30 animate-pulse' : 'bg-white/20'}`}>
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-bold text-sm sm:text-base">
                  {urgency === 'critical' ? 'Trial ending soon!' : 'Free Trial Active'}
                </p>
                <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-mono">
                  {formatTime(timeRemaining)} left
                </span>
              </div>
              <p className="text-xs sm:text-sm text-white/80">
                Full access to all features - Subscribe to keep it
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link 
              to="/subscribe" 
              className="bg-white text-slate-800 px-4 py-2 rounded-full text-sm font-bold hover:bg-slate-100 transition-all shadow-lg flex items-center gap-1"
              data-testid="trial-subscribe-btn"
            >
              <Crown className="h-4 w-4" />
              <span className="hidden sm:inline">Subscribe Now</span>
              <span className="sm:hidden">Subscribe</span>
            </Link>
            <button 
              onClick={() => setDismissed(true)}
              className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Trial expired
  if (isTrialExpired && tier === 'free') {
    return (
      <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 text-white py-3 px-4 relative" data-testid="trial-expired-banner">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/20 rounded-lg">
              <Clock className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <p className="font-bold text-sm sm:text-base">Your free trial has ended</p>
              <p className="text-xs sm:text-sm text-slate-400">
                3 articles/day - Limited features - Subscribe to unlock everything
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link 
              to="/subscribe" 
              className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold hover:from-amber-600 hover:to-orange-600 transition-all shadow-lg flex items-center gap-1"
              data-testid="expired-subscribe-btn"
            >
              <Crown className="h-4 w-4" />
              <span className="hidden sm:inline">Upgrade Now</span>
              <span className="sm:hidden">Upgrade</span>
            </Link>
            <button 
              onClick={() => setDismissed(true)}
              className="p-1.5 hover:bg-white/10 rounded-full transition-colors"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default TrialBanner;
